import { Sigma } from "lucide-react";
import { cn } from "../../lib/utils";
import { ANALYTICS_INPUT_COPY } from "../../lib/analyticsFeeds";

/**
 * The dual-role indicator: this file is a sample's evidence *and* an input to an
 * analytics workflow.
 *
 * The badge belongs where the surface has no other answer to the question. Two
 * render it, and both are chips naming a cited file: the check result card's
 * document chips, and the attribute test's Evidence Used chips. Each reports
 * what a check concluded and which files it cited, and nothing else on either
 * speaks for the flag, so without the badge the analytics half of a cited file's
 * role is unreadable there. They draw the same documents, so a badge on one and
 * not the other would have the same file reading two ways.
 *
 * A table whose row carries the `For Analytics Test` control in a column of its
 * own already answers it, and answers it better — as a decision the auditor can
 * take rather than a glyph they can only read. A badge beside the file name on
 * such a row is one bit read twice in one row, and the second reading can only
 * ever agree with the first.
 *
 * Presentational, and the whole state it needs is the flag: it never reads a
 * binding, because the flag and the binding answer different questions and this
 * one only reports the flag. The flag is not settable from here — this reports a
 * decision taken elsewhere, which is the only job it has.
 */
interface AnalyticsInputIndicatorProps {
  /**
   * `undefined` where the read the flag comes from carried none, which renders
   * the same as `false`: this indicator only ever adds the claim that a file is
   * an analytics input, and never draws the negative. Withholding it asserts
   * nothing, so it is the one reading an unanswered flag is entitled to — unlike
   * the surfaces that state a `No` or close a control on it.
   */
  flagged: boolean | undefined;
  /** Show the words beside the glyph. Off where the site is a chip. */
  withLabel?: boolean;
  className?: string;
}

export function AnalyticsInputIndicator({
  flagged,
  withLabel = false,
  className,
}: AnalyticsInputIndicatorProps) {
  if (flagged !== true) return null;
  return (
    <span
      // The compact form is a glyph with no text node, so the name is the whole
      // of what a screen reader gets. A generic element discards a name given by
      // the author; an image is named by it, and a glyph standing for a sentence
      // is what this is.
      role="img"
      aria-label={ANALYTICS_INPUT_COPY.indicatorTitle}
      title={ANALYTICS_INPUT_COPY.indicatorTitle}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded border border-stone-200 bg-stone-50 px-1.5 py-0.5 text-xs text-font-secondary",
        className,
      )}
    >
      <Sigma className="h-3 w-3" aria-hidden="true" />
      {withLabel && <span>{ANALYTICS_INPUT_COPY.indicatorTitle}</span>}
    </span>
  );
}
